import {
  DEFAULT_ACTIVE_COLORS,
  DEFAULT_COLORS_BG,
  DEFAULT_COLORS_FONT,
  DEFAULT_FONT,
  BRANDING
} from '@opengov/stories-shared/constants';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Alert from 'react-s-alert';

import { ISettings, IStoriesSettingsMessages } from '../../../../types';
import { Flash } from '../../StoriesPage';
import { Content } from '../Content';
import { Header } from '../Header';
import { useFetchSettings } from '../hooks/useFetchSettings';
import { useUpdateSettings } from '../hooks/useUpdateSettings';
import { useGeneratePreview } from '../hooks/useGeneratePreview';

import styles from './StoriesSettings.scss';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { useGetSession } from '@opengov/bnp-core';
import { useHasAdminPrivilege } from '../../../hooks/subjectClaims';
import { navigateToPublicationList } from '../../../state/navigation';

export interface IStoriesSettingsProps {
  entityName: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  messages: IStoriesSettingsMessages;
  storyMarginsEnabled: boolean;
  displayPublicationTab: boolean;
  showProgressTracker?: boolean;
  activeTab: string;
}

function setDefaultFonts(fonts: ISettings['brand']['fonts']) {
  const newFonts = { ...fonts };
  const { id } = DEFAULT_FONT;
  if (!newFonts.headline) newFonts.headline = id;
  if (!newFonts.paragraph) newFonts.paragraph = id;
  return newFonts;
}

function setDefaultColors(colors: ISettings['brand']['colors']) {
  const newColors = { ...colors };
  Object.keys(colors).forEach((section) => {
    let backgroundSwatchArray = colors[section].background.swatch;
    let fontSwatchArray = colors[section].font.swatch;
    const backgroundActiveColor = colors[section].background.active || DEFAULT_ACTIVE_COLORS[section].background;
    const fontActiveColor = colors[section].font.active || DEFAULT_ACTIVE_COLORS[section].font;
    if (backgroundSwatchArray.length < 1) backgroundSwatchArray = DEFAULT_COLORS_BG;
    if (fontSwatchArray.length < 1) fontSwatchArray = DEFAULT_COLORS_FONT;
    newColors[section].background.swatch = backgroundSwatchArray;
    newColors[section].font.swatch = fontSwatchArray;
    newColors[section].background.active = backgroundActiveColor;
    newColors[section].font.active = fontActiveColor;
  });
  return newColors;
}

function applyDefaults(settings: ISettings): ISettings {
  const { fonts, colors } = settings.brand;
  let updated = settings;

  if (!fonts.headline || !fonts.paragraph) {
    updated = { ...updated, brand: { ...updated.brand, fonts: setDefaultFonts(fonts) } };
  }

  const noSwatches =
    colors.header.background.swatch.length === 0 &&
    colors.header.font.swatch.length === 0 &&
    colors.body.background.swatch.length === 0 &&
    colors.body.font.swatch.length === 0 &&
    colors.footer.background.swatch.length === 0 &&
    colors.footer.font.swatch.length === 0;

  if (noSwatches) {
    updated = { ...updated, brand: { ...updated.brand, colors: setDefaultColors(colors) } };
  }

  return updated;
}

function StoriesSettingsInternal({
  entityId,
  entityUuid,
  entitySubdomain,
  messages,
  storyMarginsEnabled,
  displayPublicationTab,
  showProgressTracker,
  activeTab: initialActiveTab
}: IStoriesSettingsProps) {
  const { data: session } = useGetSession();
  const isAdmin = useHasAdminPrivilege(
    entityUuid && session?.uuid ? { userUuid: session.uuid, entityUuid } : null
  );
  const { data: fetchedSettings, isLoading } = useFetchSettings({ entityId });
  const { updateSettings, isUpdating } = useUpdateSettings({
    onSuccess: (_updated, params) => {
      navigateToPublicationList(params.entitySubdomain);
    }
  });
  const { generatePreview, isPreviewLoading } = useGeneratePreview({
    entityId,
    onSuccess: (previewUrl) => {
      setSettings((prev) => (prev ? { ...prev, previewUrl } : prev));
    }
  });

  const [settings, setSettings] = useState<ISettings | null>(null);
  const [activeTab, setActiveTab] = useState(initialActiveTab || BRANDING);

  // Seed local settings from server data on first load, then apply defaults
  useEffect(() => {
    if (fetchedSettings && !settings) {
      setSettings(applyDefaults(fetchedSettings));
    }
  }, [fetchedSettings, settings]);

  // Track previous settings to detect branding changes that need a preview refresh
  const prevSettingsRef = useRef<ISettings | null>(null);
  useEffect(() => {
    if (!settings) return;

    const prev = prevSettingsRef.current;
    prevSettingsRef.current = settings;

    if (!prev) {
      // First paint — generate initial preview
      generatePreview({ settings, entitySubdomain, storyMarginsEnabled });
      return;
    }

    const { fonts, colors, logo } = prev.brand;
    const { fonts: nextFonts, colors: nextColors, logo: nextLogo } = settings.brand;

    let colorsChanged = false;
    Object.keys(colors).forEach((section) => {
      if (
        colors[section].background.active !== nextColors[section].background.active ||
        colors[section].font.active !== nextColors[section].font.active
      ) {
        colorsChanged = true;
      }
    });

    if (
      logo.header !== nextLogo.header ||
      logo.footer !== nextLogo.footer ||
      !_.isEqual(logo.placement, nextLogo.placement) ||
      fonts.headline !== nextFonts.headline ||
      fonts.paragraph !== nextFonts.paragraph ||
      colorsChanged
    ) {
      generatePreview({ settings, entitySubdomain, storyMarginsEnabled });
    }
  }, [settings, entitySubdomain, storyMarginsEnabled, generatePreview]);

  const handleUpdateSettingsProps = (newSettingsProps: Partial<ISettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...newSettingsProps } : prev));
  };

  const handleUpdateSettings = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!settings) return;
    updateSettings({ settings, entitySubdomain, entityId });
  };

  if (isLoading || !settings) {
    return null;
  }

  const previewUrl = settings.previewUrl ?? '';

  return (
    <div className={styles.root}>
      <Header
        isUpdating={isUpdating}
        messages={messages}
        navigateToPublicationList={() => navigateToPublicationList(entitySubdomain)}
        updateSettings={handleUpdateSettings}
        displayPublicationTab={displayPublicationTab}
        isAdmin={isAdmin}
      />
      <div className={styles.wrapper}>
        <Content
          entityId={entityId}
          updateSettings={handleUpdateSettings}
          settings={settings}
          messages={messages}
          entitySubdomain={entitySubdomain}
          updateSettingsProps={handleUpdateSettingsProps}
          previewUrl={previewUrl}
          isPreviewLoading={isPreviewLoading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showProgressTracker={showProgressTracker}
        />
      </div>
      <Alert
        effect="scale"
        position="bottom-right"
        timeout={5000}
        offset={25}
        stack={{
          limit: 3,
          spacing: 25
        }}
        contentTemplate={Flash}
      />
    </div>
  );
}

export const StoriesSettings = (props: IStoriesSettingsProps) => {
  useEffect(() => {
    const main = document.getElementById('main');
    if (main) {
      main.style.overflow = 'hidden';
      return () => { main.style.overflow = ''; };
    }
  }, []);

  return (
    <PageErrorBoundary viewName={ViewName.StoriesSettingsPage}>
      <StoriesSettingsInternal {...props} />
    </PageErrorBoundary>
  );
};
